import { User } from '../../domain/users';
import { DateTimeStr } from '../../types/date';
import { ApiDataQuery, Paginated } from '../../types/other';
import { BaseAPIClient } from '../base_client';
import { CreateFragmentDto } from '../dto';
import { AudiogramGenerationJobDto, GetFragmentDto } from '../dto/fragments/get-fragment.dto';
import { PublicJobDto } from '../dto/jobs/public-job.dto';

export const FragmentsAPI = {
  fetchAll(
    this: BaseAPIClient,
    stationId: string,
    query: ApiDataQuery,
  ): Promise<Paginated<GetFragmentDto>> {
    return this.get(`/stations/${stationId}/fragments`, {
      params: {
        ...query,
        contain: 'createdBy',
      },
    });
  },

  fetch<T extends GetFragmentDto = GetFragmentDto>(
    this: BaseAPIClient,
    id: string,
  ): Promise<T & { createdBy: User }> {
    return this.get(`/fragments/${id}`, {
      params: {
        contain: 'createdBy',
      },
    });
  },

  generateDownloadUrl(
    this: BaseAPIClient,
    id: string,
    mediaType?: string,
  ): Promise<{ url: string; expiresAt: DateTimeStr }> {
    return this.get(`/fragments/${id}/download-url`, {
      params: {
        mediaType,
      },
    });
  },

  create(this: BaseAPIClient, data: CreateFragmentDto) {
    const { stationId, ...rest } = data;

    return this.post(`/stations/${stationId}/fragments`, rest);
  },

  edit(
    this: BaseAPIClient,
    data: Pick<GetFragmentDto, 'stationId' | 'id' | 'title' | 'description' | 'tags'>,
  ) {
    const { stationId, id, ...rest } = data;

    return this.put(`/stations/${stationId}/fragments/${id}`, rest);
  },

  delete(this: BaseAPIClient, id: string) {
    return this.delete(`/fragments/${id}`);
  },

  transcriptionStatus(this: BaseAPIClient, id: string): Promise<PublicJobDto> {
    return this.get(`/fragments/${id}/transcription`);
  },

  transcriptionStart(this: BaseAPIClient, id: string, settings = {}, retry?: boolean) {
    const retrySting = retry ? '?restart=1' : '';
    return this.post(`/fragments/${id}/transcription${retrySting}`, settings);
  },

  startExternalExport(this: BaseAPIClient, id: string, options: any) {
    return this.post(`/fragments/${id}/external-export`, options);
  },

  configUpdate(this: BaseAPIClient, stationId: string, data: any) {
    return this.put(`/fragments/config/${stationId}`, data);
  },

  audiogramGenerate(this: BaseAPIClient, id: string, data: AudiogramGenerationJobDto['data']) {
    return this.post(`/fragments/${id}/audiogram/generate`, data);
  },

  audiogramSocialExport(this: BaseAPIClient, fragmentId: string, platform: string, data: object) {
    return this.post(`/fragments/${fragmentId}/audiogram/social-export/${platform}`, data);
  },

  audiogramMediaUrl(this: BaseAPIClient, fragmentId: string, variant?: string): Promise<string> {
    return this.get(`/fragments/${fragmentId}/audiogram/media-url`, {
      params: { variant },
    }).then((d) => d.url);
  },

  patchTranscription(this: BaseAPIClient, fragmentId: string, slug: string, data: object) {
    return this.patch(`/pnt/v2/fragments/${slug}/${fragmentId}/wpwords`, data);
  },

  getTranscriptionDownload(
    this: BaseAPIClient,
    slug: string,
    customId: string,
    format: string,
    speakerId: boolean,
  ): Promise<any> {
    return this.get(`/pnt/v2/fragments/${slug}/transcribe/${customId}/subtitles`, {
      params: {
        download: true,
        format: format,
        speakerId: speakerId,
      },
    });
  },

  patchTranscriptionSpeaker(this: BaseAPIClient, fragmentId: string, slug: string, data: object) {
    return this.patch(`/pnt/v2/fragments/${slug}/diarize/${fragmentId}`, data);
  },
};
